import { type KeyboardEvent, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
    centreMs,
    dataShare,
    futureWeeksFor,
    isZoomable,
    scrollLeftForAnchor,
    scrollLeftToShow,
    type TimelineAnchor,
    type TimelineZoom,
    type TrackViewport,
} from '../helpers/timelineZoom';
import type { WeekAxis } from '../types';

import { scrollBehavior, timelineKeyTarget } from './timelineKeys';

export interface TimelineViewport {
    /** Callback refs, so measuring starts whenever the parts mount, not only on first render. */
    scrollerRef: (node: HTMLDivElement | null) => void;
    /** The scroller once mounted, for parts that follow its scroll themselves. */
    scroller: HTMLDivElement | null;
    /** The pinned label column's header, whose width the scroller's view loses. */
    labelRef: (node: HTMLDivElement | null) => void;
    /** The axis header, which spans the track. */
    axisRef: (node: HTMLDivElement | null) => void;
    /**
     * The axis's measured width, which row labels are laid out for: the drawn track less its
     * future weeks. Null until measured (and in jsdom).
     */
    trackPx: number | null;
    /** The whole drawn track, future weeks included, which the header's ticks divide. */
    drawnTrackPx: number | null;
    /** Whole weeks drawn after the axis: some while zoomed, none for a cycle that fits. */
    futureWeeks: number;
    /** The visible part of the track: the scroller less the label column. */
    viewPx: number | null;
    /**
     * Extra room after the drawn track, half the visible track, so a centre in the future
     * weeks can be restored — including at Month zoom, whose future weeks are shorter than
     * that half view on a phone.
     */
    endPadPx: number;
    /** The zoom drawn: null for a cycle short enough to fill the row. */
    zoom: TimelineZoom | null;
    setZoom: (zoom: TimelineZoom) => void;
    scrollToToday: () => void;
    /** Whether the track is wider than the scroller, so the scroller takes keyboard focus. */
    overflows: boolean;
    onScroll: () => void;
    /** Home, End, Page Up and Page Down, which the browser only gives vertical scrolling. */
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
}

interface Measured {
    trackPx: number;
    viewPx: number;
    overflows: boolean;
}

interface Parts {
    scroller: HTMLDivElement;
    label: HTMLDivElement;
    track: HTMLDivElement;
}

/** The geometry in axis pixels: `share` is the axis's part of the drawn track. */
function readViewport(parts: Parts, weekCount: number, share = 1): TrackViewport {
    const { scroller, label, track } = parts;
    return {
        trackPx: track.getBoundingClientRect().width * share,
        viewPx: scroller.clientWidth - label.getBoundingClientRect().width,
        weekCount,
        maxScroll: scroller.scrollWidth - scroller.clientWidth,
    };
}

/** A scroller laid out at zero width is hidden or not laid out (jsdom): nothing to measure. */
function measure(parts: Parts): Measured | null {
    if (parts.scroller.clientWidth <= 0) return null;
    const geometry = readViewport(parts, 0);
    return {
        trackPx: Math.round(geometry.trackPx),
        viewPx: Math.round(geometry.viewPx),
        overflows: geometry.maxScroll > 1,
    };
}

function sameMeasure(a: Measured | null, b: Measured | null): boolean {
    return a?.trackPx === b?.trackPx && a?.viewPx === b?.viewPx && a?.overflows === b?.overflows;
}

/**
 * The board's zoom and horizontal scroll. A long cycle draws its weeks at a fixed width and
 * scrolls. The view holds an anchor: the latest activity near the right edge until the user
 * scrolls, then the date they left in the middle. The anchor is applied again whenever the
 * parts are measured anew, the zoom changes or the timeline is refetched, so neither a late
 * layout nor a save moves the view; another cycle (`viewKey`) starts at the latest activity.
 */
export function useTimelineViewport(
    axis: WeekAxis,
    todayPct: number | null,
    viewKey: string
): TimelineViewport {
    const [scroller, scrollerRef] = useState<HTMLDivElement | null>(null);
    const [label, labelRef] = useState<HTMLDivElement | null>(null);
    const [track, axisRef] = useState<HTMLDivElement | null>(null);
    const parts = useMemo(
        () => (scroller && label && track ? { scroller, label, track } : null),
        [scroller, label, track]
    );
    const [measured, setMeasured] = useState<Measured | null>(null);
    const [chosenZoom, setChosenZoom] = useState<TimelineZoom>('week');
    const anchor = useRef<TimelineAnchor>({ kind: 'latest' });
    const anchoredView = useRef(viewKey);
    // Where the board last scrolled itself, so its own scroll events don't count as the user's.
    const ownScrollLeft = useRef<number | null>(null);
    const weekCount = axis.weeks.length;

    useLayoutEffect(() => {
        if (!parts) return;
        const update = () => {
            const next = measure(parts);
            setMeasured((current) => (sameMeasure(current, next) ? current : next));
        };
        update();
        // jsdom has no ResizeObserver; a hidden tab may not lay out until it is shown again.
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
        for (const element of [parts.scroller, parts.label, parts.track]) {
            observer?.observe(element);
        }
        document.addEventListener('visibilitychange', update);
        window.addEventListener('resize', update);
        return () => {
            observer?.disconnect();
            document.removeEventListener('visibilitychange', update);
            window.removeEventListener('resize', update);
        };
    }, [parts]);

    const zoom = isZoomable(weekCount, measured?.viewPx ?? null) ? chosenZoom : null;
    const futureWeeks = futureWeeksFor(zoom);
    const share = dataShare(weekCount, futureWeeks);
    // Week and month draw columns past the axis, so half a view of end padding lets a date
    // sit in the middle. Cycle zoom fits the row; the same pad would make it scroll.
    const endPadPx = (zoom === 'week' || zoom === 'month') && measured ? measured.viewPx / 2 : 0;

    // Before paint, so neither a zoom nor a refetch shows a frame scrolled to the wrong date.
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-anchors on every new measurement, zoom, axis or cycle
    useLayoutEffect(() => {
        if (anchoredView.current !== viewKey) {
            anchoredView.current = viewKey;
            anchor.current = { kind: 'latest' };
        }
        if (!parts) return;
        const geometry = readViewport(parts, weekCount, share);
        if (geometry.trackPx <= 0) return;
        parts.scroller.scrollLeft = scrollLeftForAnchor(anchor.current, axis, geometry);
        ownScrollLeft.current = parts.scroller.scrollLeft;
        // A new zoom changes what overflows before any resize observation reports it.
        const next = measure(parts);
        setMeasured((current) => (sameMeasure(current, next) ? current : next));
    }, [parts, weekCount, share, measured, zoom, axis, viewKey]);

    const onScroll = useCallback(() => {
        if (!parts) return;
        const left = parts.scroller.scrollLeft;
        if (ownScrollLeft.current !== null && Math.abs(left - ownScrollLeft.current) <= 1) return;
        ownScrollLeft.current = null;
        anchor.current = {
            kind: 'centre',
            ms: centreMs(axis, left, readViewport(parts, weekCount, share)),
        };
    }, [parts, axis, weekCount, share]);

    const scrollTo = useCallback(
        (left: (geometry: TrackViewport) => number) => {
            if (!parts) return;
            const target = left(readViewport(parts, weekCount, share));
            if (typeof parts.scroller.scrollTo === 'function') {
                parts.scroller.scrollTo({ left: target, behavior: scrollBehavior() });
            } else {
                parts.scroller.scrollLeft = target;
            }
        },
        [parts, weekCount, share]
    );

    const scrollToToday = useCallback(() => {
        if (todayPct !== null) scrollTo((geometry) => scrollLeftToShow(todayPct, geometry));
    }, [scrollTo, todayPct]);

    const onKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.target !== event.currentTarget || !parts) return;
            const target = timelineKeyTarget(event.key, parts.scroller.scrollLeft, todayPct);
            if (target === null) return;
            event.preventDefault();
            scrollTo(target);
        },
        [parts, scrollTo, todayPct]
    );

    return {
        scroller,
        scrollerRef,
        labelRef,
        axisRef,
        trackPx: measured ? measured.trackPx * share : null,
        drawnTrackPx: measured?.trackPx ?? null,
        futureWeeks,
        viewPx: measured?.viewPx ?? null,
        endPadPx,
        zoom,
        setZoom: setChosenZoom,
        scrollToToday,
        overflows: measured?.overflows ?? false,
        onScroll,
        onKeyDown,
    };
}
